import styled, { css } from 'styled-components';

import {
  MESSAGE_ORDER_TYPE_FIRST,
  MESSAGE_ORDER_TYPE_LAST,
  MESSAGE_ORDER_TYPE_MIDDLE,
  MESSAGE_TYPE_SENT
} from '../../utilities';

/**
 * Returns the value for the css property `border-radius`
 * depending on the type of message and its order.
 */
export function getBorderRadius(borderRadius = 0, orderType, messageType) {
  switch (orderType) {
    case MESSAGE_ORDER_TYPE_FIRST:
      return messageType === MESSAGE_TYPE_SENT
        ? `${borderRadius}px ${borderRadius}px 0 ${borderRadius}px`
        : `${borderRadius}px ${borderRadius}px ${borderRadius}px 0`;
    case MESSAGE_ORDER_TYPE_MIDDLE:
      return messageType === MESSAGE_TYPE_SENT
        ? `${borderRadius}px 0 0 ${borderRadius}px`
        : `0 ${borderRadius}px ${borderRadius}px 0`;
    case MESSAGE_ORDER_TYPE_LAST:
      return messageType === MESSAGE_TYPE_SENT
        ? `${borderRadius}px 0 ${borderRadius}px ${borderRadius}px`
        : `0 ${borderRadius}px ${borderRadius}px ${borderRadius}px`;
    default:
      return 'none';
  }
}

export const MessageBase = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  width: 100%;
  ${({ isDivider }) =>
    isDivider &&
    css`
      border-top: 1px solid #c4d1d8;
    `};
`;

export const MessageBaseContent = styled.div`
  width: ${props => (props.isCarousel ? '100%' : 'auto')};
  max-width: ${props => (props.isCarousel ? '100% !important' : '90% !important')};
  padding: 6px 16px;
  margin-top: 0;
  margin-right: 0;
  margin-bottom: 0;
  margin-left: ${({ type }) => (type === MESSAGE_TYPE_SENT ? 'auto' : 0)};
  font-family: ${({ textFontFamily }) => textFontFamily};
  font-size: ${({ textFontSize }) => textFontSize}px;
  font-weight: 400;
  line-height: 20px;
  word-wrap: break-word;
  text-align: left;
  box-shadow: ${props => (props.background ? '0 1px 4px 0 rgba(0, 0, 0, 0.1)' : 'none')};
  border-radius: ${({ borderRadius, orderType, type }) =>
    getBorderRadius(borderRadius, orderType, type)};
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  position: relative;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);

  ${({ disablePadding, isCarousel }) => {
    if (disablePadding) {
      return css`
        padding: 0;
        border-radius: 0;
        box-shadow: none;
      `;
    }
    if (!isCarousel) {
      return css`
        & * {
          font-size: ${({ textFontSize }) => textFontSize}px !important;
        }
        p:not(:last-of-type) {
          margin: 0 0 10px;
        }
      `;
    }

    return null;
  }}

  & * {
    font-family: ${({ textFontFamily }) => textFontFamily} !important;
  }

  .iframe-wrap {
    position: relative;

    & > iframe {
      width: 100% !important;
      height: auto !important;
      max-height: 100% !important;
      margin: 0 !important;
    }
  }

  .container-with-iframe {
    max-width: 100% !important;

    iframe {
      position: relative !important;
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
    }
  }

  table {
    width: 100%;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    border-collapse: collapse;
    p {
      margin: 0;
      text-align: left;
    }
  }

  img {
    position: static;
    display: block;
    width: 100% !important;
    height: auto !important;
    margin: 8px 0 0 !important;
    border-radius: 4px;
  }

  img.emoji {
    display: inline-block;
    width: 15px !important;
    margin: 0 !important;
    vertical-align: middle;
  }

  a {
    text-decoration: underline;
    color: #2873c0 !important;
    box-shadow: none;
    transition: none;
  }

  a:hover,
  a:focus,
  a:link {
    text-decoration: underline;
    color: #2873c0 !important;
  }

  a::before,
  a::after {
    content: '';
    display: none;
  }

  ul {
    padding: 20px 0px 20px 40px;
    margin: 0;
    list-style: initial;
  }

  ul li {
    padding: initial;
    margin: 0;
    font: inherit;
  }

  .form-container {
    div {
      margin-bottom: 6px;
    }

    & input:not([type='submit']),
    & textarea {
      min-width: 30%;
      max-width: 100%;
      padding: 10px 8px;
      font-size: 14px;
      background-color: #fff;
      border: 1px solid ${props => props.textColor};
      border-radius: ${({ borderRadius }) => borderRadius}px;
    }
    & input[type='text'],
    & input[type='email'] {
      width: 100%;
    }
    & input[type='checkbox'],
    & input[type='radio'] {
      min-width: auto;
      margin-top: 0;
      margin-bottom: 0;
      vertical-align: middle;
    }
    textarea {
      width: 100%;
      resize: none;
    }
    select {
      min-width: 30%;
      max-width: 100%;
      padding: 10px 8px;
      font-size: 14px;
      background-color: #fff;
      border: 1px solid ${props => props.textColor};
      border-radius: ${({ borderRadius }) => borderRadius}px;
      outline: none;
    }
    input[type='submit'] {
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${props => props.buttonColor};
      background-color: ${props => props.buttonBackgroundColor};
      border: 1px solid ${props => props.buttonBackgroundColor};
      border-radius: ${props => props.borderRadius}px;
      transition: color 0.2s ease-in-out, background-color 0.3s ease-in-out;
      cursor: pointer;
      &:hover:not(:disabled) {
        color: ${props => props.buttonBackgroundColor};
        background-color: ${props => props.buttonColor};
      }
    }

    input,
    textarea {
      &::placeholder {
        font-style: italic;
        color: #9a9a9a;
        opacity: 1;
      }
    }

    input:disabled,
    select:disabled,
    textarea:disabled {
      opacity: 0.5;
      cursor: default;
    }

    input[type='radio'] {
      width: auto;
      height: auto;
      min-height: auto;
      appearance: radio;
      -webkit-appearance: radio;
      -moz-appearance: radio;
    }

    input[type='radio']::before {
      display: none;
    }
  }

  video {
    width: 100%;
    min-width: 200px;
    height: auto;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    span > li {
      list-style-position: inside;
    }
  }
`;
